import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';

import { Action, createReducer, on } from '@ngrx/store';

import { PigContextKeys } from '../../../shared/models/shared.model';
import {
  Barn,
  DosatorSetting,
  EnergySystem,
  LysineSystem,
  SettingModuleState,
} from '../../models/settings.model';
import { LeeOAuthentication } from '../../models/third-parties.model';
import { setUomSetting } from '../actions/settings.actions';
import * as settingActions from '../actions/settings.actions';

function selectIdBarnNumber(b: Barn): number {
  return b.id;
}

function sortBarnByName(a: Barn, b: Barn): number {
  return a.name.localeCompare(b.name);
}

function selectIdDosatorSettingNumber(d: DosatorSetting): number {
  return d.id;
}

function sortDosatorSettingByName(ds: DosatorSetting, d: DosatorSetting): number {
  return ds.name.localeCompare(d.name);
}

export const barnAdapter: EntityAdapter<Barn> = createEntityAdapter({
  selectId: selectIdBarnNumber,
  sortComparer: sortBarnByName,
});

export const dosatorSettingAdapter: EntityAdapter<DosatorSetting> = createEntityAdapter({
  selectId: selectIdDosatorSettingNumber,
  sortComparer: sortDosatorSettingByName,
});

export const initialState = {
  barns: barnAdapter.getInitialState([]),
  selectedBarn: <Barn>{},
  silos: null,
  energySystem: null,
  lysineSystem: null,
  discount: null,
  nedapInstallations: null,
  leeOAuthentication: null,
  dosatorSettings: dosatorSettingAdapter.getInitialState([]),
  selectedDosatorSetting: <DosatorSetting>{},
  isLoaded: false,
} as SettingModuleState;

const settingReducer = createReducer(
  initialState,
  on(setUomSetting, (state, { system, value }) => ({
    ...state,
    energySystem:
      system === PigContextKeys.UOM_ENERGY_SYSTEM
        ? (value as EnergySystem)
        : (state.energySystem as EnergySystem),
    lysineSystem:
      system === PigContextKeys.UOM_LYSINE_SYSTEM
        ? (value as LysineSystem)
        : (state.lysineSystem as LysineSystem),
    isLoaded: true,
  })),
  on(settingActions.loadBarnsSuccess, (state, { barns }) => ({
    ...state,
    selectedBarn: <Barn>{},
    barns: barnAdapter.setAll(barns, state.barns),
    isLoaded: true,
  })),
  on(settingActions.upsertBarnSuccess, (state, { barn }) => ({
    ...state,
    selectedBarn: barn,
    barns: barnAdapter.upsertOne(barn, state.barns),
  })),
  on(settingActions.deleteBarnSuccess, (state, { barn }) => ({
    ...state,
    selectedBarn: <Barn>{},
    barns: barnAdapter.removeOne(barn.id, state.barns),
  })),
  on(settingActions.upsertBarn, (state, { barn }) => ({
    ...state,
    selectedBarn: barn,
  })),
  on(settingActions.setSelectedBarn, (state, { barnId }) => ({
    ...state,
    selectedBarn:
      !!barnId && !!state.barns.entities[barnId] ? state.barns.entities[barnId] : <Barn>{},
  })),
  on(settingActions.loadSilosSuccess, (state, { silos }) => ({
    ...state,
    silos,
    isLoaded: true,
  })),
  on(settingActions.loadDiscountSuccess, (state, { discount }) => ({
    ...state,
    discount,
    isLoaded: true,
  })),
  on(settingActions.loadNedapInstallationsSuccess, (state, { nedapInstallations }) => ({
    ...state,
    nedapInstallations,
    isLoaded: true,
  })),
  on(settingActions.loadLeeOAuthenticationSuccess, (state, { leeOAuthentication }) => ({
    ...state,
    leeOAuthentication,
    isLoaded: true,
  })),
  on(settingActions.deleteLeeOAuthenticationSuccess, (state, _) => ({
    ...state,
    leeOAuthentication: undefined as LeeOAuthentication,
  })),
  on(settingActions.loadDosatorSettingsSuccess, (state, { dosatorSettings }) => ({
    ...state,
    selectedDosatorSetting: <DosatorSetting>{},
    dosatorSettings: dosatorSettingAdapter.setAll(dosatorSettings, state.dosatorSettings),
    isLoaded: true,
  })),
  on(settingActions.upsertDosatorSettingSuccess, (state, { dosatorSetting: dosatorSetting }) => ({
    ...state,
    selectedDosatorSetting: dosatorSetting,
    dosatorSettings: dosatorSettingAdapter.upsertOne(dosatorSetting, state.dosatorSettings),
  })),
  on(settingActions.deleteDosatorSettingSuccess, (state, { dosatorSetting: dosatorSetting }) => ({
    ...state,
    selectedDosatorSetting: <DosatorSetting>{},
    dosatorSettings: dosatorSettingAdapter.removeOne(dosatorSetting.id, state.dosatorSettings),
  })),
  on(settingActions.upsertDosatorSetting, (state, { dosatorSetting }) => ({
    ...state,
    selectedDosatorSetting: dosatorSetting,
  })),
  on(settingActions.setSelectedDosatorSetting, (state, { dosatorSettingId }) => ({
    ...state,
    selectedDosatorSetting:
      !!dosatorSettingId && !!state.dosatorSettings.entities[dosatorSettingId]
        ? state.dosatorSettings.entities[dosatorSettingId]
        : <DosatorSetting>{},
  })),
);

export function reducer(state: SettingModuleState | undefined, action: Action) {
  return settingReducer(state, action);
}
